/** iframe 内归档失败通知父页打开确认弹窗 */
export const OSS_ARCHIVE_PARENT_MESSAGE_TYPE = "OSS_ARCHIVE_NEED_RESTORE";

export function isOssArchiveEmbeddedFrame(): boolean {
	return typeof window !== "undefined" && !!window.parent && window.parent !== window;
}

/** OSS 归档 error_code（PRD §5） */
export const OSS_ARCHIVE_ERROR_CODES = [
	"OSS_ARCHIVED_NEED_RESTORE",
	"OSS_RESTORE_PENDING",
	"OSS_RESTORE_READY",
	"OSS_RESTORE_EXPIRED",
	"OSS_RESTORE_FAILED",
	"OSS_ARCHIVE_FORBIDDEN",
	"OSS_ARCHIVE_INTEGRATION_DISABLED"
] as const;

export type OssArchiveErrorCode = (typeof OSS_ARCHIVE_ERROR_CODES)[number];

export type OssArchiveRestoreStatus =
	| "none"
	| "pending"
	| "restoring"
	| "ready"
	| "expired"
	| "failed";

export interface OssArchiveErrorData {
	error_code?: string;
	pid?: number;
	order_id?: number;
	object_keys?: string[];
	restore_status?: OssArchiveRestoreStatus | string;
	[key: string]: unknown;
}

export interface OssArchiveRejectError {
	code?: number;
	message?: string;
	data?: OssArchiveErrorData;
	error_code?: string;
}

export function getOssArchiveErrorCode(payload?: OssArchiveErrorData | null): string | undefined {
	if (!payload?.error_code) {
		return undefined;
	}
	return String(payload.error_code);
}

export function isOssArchiveErrorCode(code?: string | null): code is OssArchiveErrorCode {
	if (!code) {
		return false;
	}
	return (OSS_ARCHIVE_ERROR_CODES as readonly string[]).includes(code);
}

export function isOssArchiveRejectError(error: unknown): error is OssArchiveRejectError {
	if (!error || typeof error !== "object") {
		return false;
	}
	const err = error as OssArchiveRejectError;
	const code = err.error_code || getOssArchiveErrorCode(err.data);
	return isOssArchiveErrorCode(code);
}

export function normalizeOssArchiveError(error: unknown): OssArchiveRejectError | null {
	if (!isOssArchiveRejectError(error)) {
		return null;
	}
	const err = error as OssArchiveRejectError;
	const errorCode = err.error_code || getOssArchiveErrorCode(err.data);
	if (!isOssArchiveErrorCode(errorCode)) {
		return null;
	}
	return {
		...err,
		error_code: errorCode,
		data: {
			...(err.data || {}),
			error_code: errorCode
		}
	};
}

export function pickOssArchiveRejectFromSettled(
	...results: PromiseSettledResult<unknown>[]
): OssArchiveRejectError | null {
	for (const result of results) {
		if (result.status === "rejected") {
			const archive = normalizeOssArchiveError(result.reason);
			if (archive) {
				return archive;
			}
		}
	}
	return null;
}

/** 预览 catch 用：普通失败当成空数据，归档错误原样抛出给弹窗。 */
export function emptyUnlessOssArchive(error: unknown): [] {
	const archive = normalizeOssArchiveError(error);
	if (archive) {
		throw archive;
	}
	return [];
}

export function mapErrorCodeToDialogState(
	errorCode: string,
	restoreStatus?: string
): "need" | "pending" | "ready" | "expired" | "failed" | "forbidden" {
	if (errorCode === "OSS_ARCHIVE_FORBIDDEN") {
		return "forbidden";
	}
	// 授权提交后以本地 restore_status 为准（原 error_code 仍可能是 need/expired）
	if (restoreStatus === "ready") {
		return "ready";
	}
	if (restoreStatus === "failed") {
		return "failed";
	}
	if (restoreStatus === "pending" || restoreStatus === "restoring") {
		return "pending";
	}
	if (errorCode === "OSS_RESTORE_READY") {
		return "ready";
	}
	if (errorCode === "OSS_RESTORE_EXPIRED" || restoreStatus === "expired") {
		return "expired";
	}
	if (errorCode === "OSS_RESTORE_FAILED") {
		return "failed";
	}
	if (errorCode === "OSS_RESTORE_PENDING") {
		return "pending";
	}
	return "need";
}
